import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query"
import {
  fetchKanbanCards,
  getOpportunityDetail,
  listOpportunities,
  markAsInterested,
  updateKanbanStage,
  type KanbanCard,
  type Opportunity,
  type OpportunityFilters,
  type PaginatedOpportunities,
} from "../services/sourcingApi"

export const useOpportunities = (portfolioId: number | undefined, page: number) => {
  return useQuery({
    queryKey: ["opportunities", portfolioId, page],
    queryFn: () => listOpportunities(portfolioId!, page),
    enabled: !!portfolioId && !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000,
  })
}

export const useInfiniteOpportunities = (
  portfolioId: number | undefined,
  filters: OpportunityFilters = {}
) => {
  return useInfiniteQuery({
    queryKey: ["opportunities", "infinite", portfolioId, filters],
    queryFn: ({ pageParam }) =>
      listOpportunities(portfolioId!, pageParam as number, filters),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.next !== null ? (lastPageParam as number) + 1 : undefined,
    enabled: !!portfolioId && !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000,
  })
}

export const useOpportunityDetail = (id: number) => {
  return useQuery({
    queryKey: ["opportunityDetail", id],
    queryFn: () => getOpportunityDetail(id),
    enabled: id > 0 && !!localStorage.getItem("token"),
    staleTime: 5 * 60 * 1000,
  })
}

export const useKanbanCards = (portfolioId: number | undefined) => {
  return useQuery({
    queryKey: ["kanbanCards", portfolioId],
    queryFn: () => fetchKanbanCards(portfolioId!),
    enabled: !!portfolioId && !!localStorage.getItem("token"),
    staleTime: 0,
  })
}

export const useUpdateKanbanStage = (portfolioId: number | undefined) => {
  const queryClient = useQueryClient()
  const queryKey = ["kanbanCards", portfolioId]

  return useMutation({
    mutationFn: ({ id, kanbanStage }: { id: number; kanbanStage: string }) =>
      updateKanbanStage(id, kanbanStage),
    onMutate: async ({ id, kanbanStage }) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<KanbanCard[]>(queryKey)
      queryClient.setQueryData<KanbanCard[]>(queryKey, (old) =>
        old?.map((card) =>
          card.id === id ? { ...card, kanbanStage } : card
        )
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
    },
  })
}

export const useMarkAsInterested = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => markAsInterested(id),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: ["opportunities", "infinite"] })
      const previousData = queryClient.getQueriesData<InfiniteData<PaginatedOpportunities>>({
        queryKey: ["opportunities", "infinite"],
      })
      queryClient.setQueriesData<InfiniteData<PaginatedOpportunities>>(
        { queryKey: ["opportunities", "infinite"] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              count: page.count - 1,
              results: page.results.filter((o) => o.id !== id),
            })),
          }
        }
      )
      return { previousData }
    },
    onError: (_err, _id, context) => {
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSuccess: (updatedOpportunity: Opportunity) => {
      queryClient.invalidateQueries({ queryKey: ["opportunityDetail", updatedOpportunity.id] })
      queryClient.invalidateQueries({ queryKey: ["kanbanCards"] })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["opportunities", "infinite"] })
    },
  })
}
